/**
 * <LiveSessionMenu> — the overflow menu of an upcoming live (molecule).
 *
 * Extracted from `<LiveSessionCard>` so the three surfaces that show a session — the card,
 * the home hero, the compact row — share ONE set of rows. The rules stay the ones the card
 * had: the labels/icons are an internal const (no call-site passes rows in), the visible set
 * is derived from the session's own `statuses`, and a finished session shows no menu at all.
 *
 * Renders nothing when `onAction` is omitted, or when the derived set is empty.
 */
import React from "react"

import { type IconName } from "../../atoms/icons/Icon"
import { ThreeDotMenu } from "../ThreeDotMenu"
import { type UILiveSessionStatus } from "../../../ports/primary/ui/types"

/** An overflow-menu action a live surface can emit (mirrors the legacy `buildMenuItems` rows). */
export type LiveMenuAction = "manage_rdv" | "message_tutor" | "cancel_absence" | "cancel_late" | "cancel_recovery"

/** Fixed label + FA5 icon per action — the menu never receives its rows from the caller. */
const MENU_ITEMS: Record<LiveMenuAction, { text: string; icon: IconName }> = {
    manage_rdv: { text: "Gérer le rdv", icon: "calendar-alt" },
    message_tutor: { text: "Message au tuteur", icon: "envelope" },
    cancel_absence: { text: "Annuler l'absence", icon: "times-circle" },
    cancel_late: { text: "Annuler le retard", icon: "times-circle" },
    cancel_recovery: { text: "Annuler le rattrapage", icon: "times-circle" },
}

/** Menu rows from the stacked statuses (absent wins; then late; then recovery). */
export function visibleActions(statuses: UILiveSessionStatus[]): LiveMenuAction[] {
    if (statuses.includes("finished")) return []
    if (statuses.includes("absent")) return ["cancel_absence", "message_tutor"]
    if (statuses.includes("late")) return ["manage_rdv", "message_tutor", "cancel_late"]
    if (statuses.includes("makeup_recovery")) return ["manage_rdv", "message_tutor", "cancel_recovery"]
    return ["manage_rdv", "message_tutor"]
}

export type LiveSessionMenuProps = {
    statuses: UILiveSessionStatus[]
    /** Emitted when a row is tapped. When omitted, the menu is not rendered. */
    onAction?: (action: LiveMenuAction) => void
    /** Trigger footprint in px — forwarded to `<ThreeDotMenu>`. */
    size?: number
    testID?: string
}

export function LiveSessionMenu(props: LiveSessionMenuProps) {
    const { statuses, onAction, size, testID } = props
    const actions = onAction != null ? visibleActions(statuses) : []
    if (actions.length === 0) return null

    return (
        <ThreeDotMenu
            size={size}
            testID={testID}
            items={actions.map((action) => ({
                ...MENU_ITEMS[action],
                onPress: () => onAction?.(action),
            }))}
        />
    )
}
